import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Pencil, Trash2 } from "./icons";
import { useAuth } from "../context/AuthContext";
import { COMMON_SPEC_FIELDS, SUBCATEGORY_SPEC_FIELDS } from "../data/specFields";
import {
  CATEGORY_LABELS,
  CATEGORY_SUBCATEGORIES,
  type Product,
  type ProductCategory,
} from "../types";
import SectionHeading from "./SectionHeading";
import AdminTabs from "./AdminTabs";
import AdminSectionErrorBoundary from "./AdminSectionErrorBoundary";

const CATEGORIES = Object.keys(CATEGORY_LABELS) as ProductCategory[];
const PAGE_SIZE = 25;

interface FormState {
  id: string;
  category: ProductCategory;
  subcategory: string;
  brand: string;
  name: string;
  model: string;
  image: string;
  specs: string;
  characteristics: Record<string, string>;
  price: string;
  stockQuantity: string;
  onSale: boolean;
  description: string;
  images: string;
  brochureUrl: string;
  videoId: string;
}

const EMPTY_FORM: FormState = {
  id: "",
  category: "Thermal",
  subcategory: "",
  brand: "",
  name: "",
  model: "",
  image: "",
  specs: "",
  characteristics: {},
  price: "",
  stockQuantity: "",
  onSale: false,
  description: "",
  images: "",
  brochureUrl: "",
  videoId: "",
};

function productToForm(product: Product): FormState {
  const characteristics: Record<string, string> = {};
  for (const [key, value] of Object.entries(product.characteristics ?? {})) {
    characteristics[key] = String(value);
  }
  return {
    id: product.id,
    category: product.category,
    subcategory: product.subcategory ?? "",
    brand: product.brand ?? "",
    name: product.name,
    model: product.model,
    image: product.image,
    specs: product.specs.join("\n"),
    characteristics,
    price: product.price !== undefined ? String(product.price) : "",
    stockQuantity: product.stockQuantity !== undefined ? String(product.stockQuantity) : "",
    onSale: !!product.onSale,
    description: product.description ?? "",
    images: (product.images ?? []).join("\n"),
    brochureUrl: product.brochureUrl ?? "",
    videoId: product.videoId ?? "",
  };
}

export default function AdminDashboard() {
  const { user, isLoading: isAuthLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageManagerError, setImageManagerError] = useState("");
  const [isUploadingBrochure, setIsUploadingBrochure] = useState(false);
  const [brochureError, setBrochureError] = useState("");

  const loadProducts = () => {
    setIsLoadingProducts(true);
    // The live DB, not the /products.json build snapshot the customer-facing
    // catalog reads (a deliberate perf choice there, wrong here) — the admin
    // needs to see whatever the DB actually holds right now, including
    // anything added/edited since the last deploy regenerated that file.
    fetch("/api/products")
      .then((res) => res.json())
      .then((data: Product[]) => setProducts(data))
      .catch(() => {})
      .finally(() => setIsLoadingProducts(false));
  };

  useEffect(() => {
    if (user?.role === "admin") loadProducts();
  }, [user]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setError("");
    setIsFormOpen(false);
  };

  const startEdit = (product: Product) => {
    setForm(productToForm(product));
    setEditingId(product.id);
    setError("");
    setIsFormOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Удалить это оборудование из каталога?")) return;
    setDeleteError("");
    try {
      const res = await fetch(`/api/products/${encodeURIComponent(id)}`, { method: "DELETE" });
      if (res.ok) {
        setProducts((prev) => prev.filter((p) => p.id !== id));
        if (editingId === id) resetForm();
        return;
      }
      // A failed delete used to fail completely silently here — the admin
      // would click, confirm, and see nothing happen with no indication why
      // (session expired, product already gone, server error). Surfacing
      // the actual response lets them tell those apart instead of just
      // retrying blindly.
      if (res.status === 401 || res.status === 403) {
        setDeleteError("Сессия истекла. Войдите в админ-панель заново и повторите удаление.");
      } else if (res.status === 404) {
        setProducts((prev) => prev.filter((p) => p.id !== id));
        setDeleteError("Этот товар уже удалён — обновляю список.");
      } else {
        setDeleteError(`Не удалось удалить товар (ошибка сервера ${res.status}). Попробуйте ещё раз.`);
      }
    } catch (e) {
      setDeleteError("Не удалось удалить товар. Проверьте соединение с сервером и повторите попытку.");
    }
  };

  // Uploading/deleting a photo updates both the open form (so the visible
  // manual path fields stay truthful) and the products list (so the catalog
  // list below reflects it without a reload).
  const applyImageResult = (productId: string, result: { image: string; images: string[] }) => {
    setProducts((prev) =>
      prev.map((p) => (p.id === productId ? { ...p, image: result.image, images: result.images } : p))
    );
    setForm((f) =>
      f.id === productId ? { ...f, image: result.image, images: result.images.join("\n") } : f
    );
  };

  const handleImageUpload = async (productId: string, file: File) => {
    setImageManagerError("");
    setIsUploadingImage(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(`/api/products/${productId}/images`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setImageManagerError(body?.error ?? "Не удалось загрузить изображение.");
        return;
      }
      applyImageResult(productId, (await res.json()) as { image: string; images: string[] });
    } catch {
      setImageManagerError("Не удалось загрузить изображение. Проверьте соединение с сервером.");
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageDelete = async (productId: string, url: string) => {
    setImageManagerError("");
    try {
      const res = await fetch(`/api/products/${productId}/images?url=${encodeURIComponent(url)}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setImageManagerError(body?.error ?? "Не удалось удалить изображение.");
        return;
      }
      applyImageResult(productId, (await res.json()) as { image: string; images: string[] });
    } catch {
      setImageManagerError("Не удалось удалить изображение. Проверьте соединение с сервером.");
    }
  };

  const handleImageReorder = async (productId: string, currentImages: string[], from: number, to: number) => {
    if (to < 0 || to >= currentImages.length) return;
    const reordered = [...currentImages];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    // Optimistic — the first image is the primary shot, so moving one to
    // the front should be visible immediately, not after a round trip.
    applyImageResult(productId, { image: reordered[0], images: reordered });
    setImageManagerError("");
    try {
      const res = await fetch(`/api/products/${productId}/images/order`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ images: reordered }),
      });
      if (!res.ok) {
        // Roll back to the pre-reorder order on failure.
        applyImageResult(productId, { image: currentImages[0], images: currentImages });
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setImageManagerError(body?.error ?? "Не удалось изменить порядок изображений.");
      }
    } catch {
      applyImageResult(productId, { image: currentImages[0], images: currentImages });
      setImageManagerError("Не удалось изменить порядок изображений. Проверьте соединение с сервером.");
    }
  };

  const applyBrochureResult = (productId: string, brochureUrl: string | undefined) => {
    setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, brochureUrl } : p)));
    setForm((f) => (f.id === productId ? { ...f, brochureUrl: brochureUrl ?? "" } : f));
  };

  const handleBrochureUpload = async (productId: string, file: File) => {
    setBrochureError("");
    setIsUploadingBrochure(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(`/api/products/${productId}/brochure`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setBrochureError(body?.error ?? "Не удалось загрузить PDF.");
        return;
      }
      const result = (await res.json()) as { brochureUrl: string };
      applyBrochureResult(productId, result.brochureUrl);
    } catch {
      setBrochureError("Не удалось загрузить PDF. Проверьте соединение с сервером.");
    } finally {
      setIsUploadingBrochure(false);
    }
  };

  const handleBrochureDelete = async (productId: string) => {
    setBrochureError("");
    try {
      const res = await fetch(`/api/products/${productId}/brochure`, { method: "DELETE" });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setBrochureError(body?.error ?? "Не удалось удалить PDF.");
        return;
      }
      applyBrochureResult(productId, undefined);
    } catch {
      setBrochureError("Не удалось удалить PDF. Проверьте соединение с сервером.");
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    const specs = form.specs
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    const images = form.images
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    const specFieldDefs = [...COMMON_SPEC_FIELDS, ...(SUBCATEGORY_SPEC_FIELDS[form.subcategory] ?? [])];
    const knownFieldKeys = new Set(specFieldDefs.map((field) => field.key));
    const characteristics: Record<string, string | number> = {};
    for (const field of specFieldDefs) {
      const raw = form.characteristics[field.key];
      if (raw === undefined || raw === "") continue;
      characteristics[field.key] = field.type === "number" ? Number(raw) : raw;
    }
    // Preserve any characteristic the product already had that isn't one of
    // this subcategory's known fields (e.g. Tognana's "collection") — the
    // form has no input for it, but dropping it on every save would erase
    // data the admin never actually touched.
    for (const [key, raw] of Object.entries(form.characteristics)) {
      if (knownFieldKeys.has(key) || raw === undefined || raw === "") continue;
      characteristics[key] = raw;
    }

    const payload = {
      id: form.id.trim(),
      category: form.category,
      subcategory: form.subcategory || undefined,
      brand: form.brand.trim() || undefined,
      name: form.name.trim(),
      model: form.model.trim(),
      image: form.image.trim(),
      specs,
      characteristics: Object.keys(characteristics).length > 0 ? characteristics : undefined,
      price: form.price.trim() === "" ? undefined : Number(form.price),
      stockQuantity: form.stockQuantity.trim() === "" ? undefined : Number(form.stockQuantity),
      onSale: form.onSale,
      description: form.description.trim(),
      images,
      videoId: form.videoId.trim() || undefined,
    };

    setIsSaving(true);
    let res;
    try {
      res = await fetch(editingId ? `/api/products/${editingId}` : "/api/products", {
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setIsSaving(false);

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        setError(data?.error ?? "Не удалось сохранить оборудование");
        return;
      }
    } catch (e) {
      setIsSaving(false);
      setError("Не удалось сохранить оборудование. Проверьте соединение с сервером.");
      return;
    }

    const saved = (await res.json()) as Product;
    setProducts((prev) =>
      editingId ? prev.map((p) => (p.id === editingId ? saved : p)) : [...prev, saved]
    );
    resetForm();
  };

  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return products;
    return products.filter((product) =>
      [product.id, product.name, product.model, product.brand, product.subcategory]
        .filter((value): value is string => typeof value === "string")
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [products, searchQuery]);

  // Reset to page 1 whenever the search narrows/widens the result set —
  // otherwise a search could leave the pager stranded past the end.
  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const pageCount = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const pageItems = filteredProducts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Every hook above must run on every render regardless of auth state —
  // this early return has to come after all of them (Rules of Hooks). It
  // used to sit right after the loadProducts effect, before filteredProducts
  // (useMemo) and the page-reset effect existed; once those were added
  // after it, the component called a different number of hooks depending on
  // whether this guard fired, which is exactly React error #300 ("Rendered
  // fewer hooks than expected") — the crash reported after logging out and
  // on a fresh unauthenticated visit to /admin.
  if (!isAuthLoading && user?.role !== "admin") {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <section className="bg-white pb-24 pt-40 md:pb-32">
      <div className="container-industrial">
        <div className="flex items-center justify-end gap-4">
          <span className="text-sm text-steel-500">Вы вошли как {user?.name}</span>
          <button
            type="button"
            onClick={async () => {
              await logout();
              navigate("/");
            }}
            className="btn-dark-outline shrink-0"
          >
            Выйти
          </button>
        </div>

        <div className="mt-6">
          <AdminTabs />
        </div>

        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <SectionHeading title="Управление оборудованием" variant="sans" size="md" />
          {!isFormOpen && (
            <button
              type="button"
              onClick={() => setIsFormOpen(true)}
              className="btn-primary shrink-0"
            >
              + Добавить оборудование
            </button>
          )}
        </div>

        {isFormOpen && (
        <form
          onSubmit={handleSubmit}
          className="mt-12 grid grid-cols-1 gap-5 border border-steel-200 p-8 sm:grid-cols-2"
        >
          <h3 className="font-sans text-xl font-semibold tracking-tight text-ink sm:col-span-2">
            {editingId ? `Редактирование: ${editingId}` : "Новое оборудование"}
          </h3>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-steel-500">
              ID (латиницей, без пробелов)
            </span>
            <input
              required
              disabled={!!editingId}
              type="text"
              value={form.id}
              onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
              className="border border-steel-300 px-3 py-2.5 text-sm text-ink outline-none focus:border-ink disabled:bg-steel-50 disabled:text-steel-400"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-steel-500">
              Категория
            </span>
            <select
              value={form.category}
              onChange={(e) => {
                const category = e.target.value as ProductCategory;
                setForm((f) => ({ ...f, category, subcategory: "", characteristics: {} }));
              }}
              className="border border-steel-300 bg-white px-3 py-2.5 text-sm text-ink outline-none focus:border-ink"
            >
              {CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {CATEGORY_LABELS[category]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-steel-500">
              Подкатегория
            </span>
            <select
              value={form.subcategory}
              onChange={(e) =>
                setForm((f) => ({ ...f, subcategory: e.target.value, characteristics: {} }))
              }
              className="border border-steel-300 bg-white px-3 py-2.5 text-sm text-ink outline-none focus:border-ink"
            >
              <option value="">— не выбрано —</option>
              {(CATEGORY_SUBCATEGORIES[form.category] ?? []).map((sub) => (
                <option key={sub} value={sub}>
                  {sub}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-steel-500">
              Бренд
            </span>
            <input
              type="text"
              value={form.brand}
              onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
              className="border border-steel-300 px-3 py-2.5 text-sm text-ink outline-none focus:border-ink"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-steel-500">
              Цена, ₸
            </span>
            <input
              type="number"
              min="0"
              step="1"
              value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
              placeholder="Оставьте пустым — «цена по запросу»"
              className="border border-steel-300 px-3 py-2.5 text-sm text-ink outline-none focus:border-ink"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-steel-500">
              В наличии, шт.
            </span>
            <input
              type="number"
              min="0"
              step="1"
              value={form.stockQuantity}
              onChange={(e) => setForm((f) => ({ ...f, stockQuantity: e.target.value }))}
              placeholder="Оставьте пустым — «под заказ»"
              className="border border-steel-300 px-3 py-2.5 text-sm text-ink outline-none focus:border-ink"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-steel-500">
              Название
            </span>
            <input
              required
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="border border-steel-300 px-3 py-2.5 text-sm text-ink outline-none focus:border-ink"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-steel-500">
              Модель
            </span>
            <input
              required
              type="text"
              value={form.model}
              onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
              className="border border-steel-300 px-3 py-2.5 text-sm text-ink outline-none focus:border-ink"
            />
          </label>

          <label className="flex flex-col gap-1.5 sm:col-span-2">
            <span className="text-sm font-semibold text-steel-500">
              Путь к изображению
            </span>
            <input
              required
              type="text"
              placeholder="/assets/products/thermal-01.svg"
              value={form.image}
              onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))}
              className="border border-steel-300 px-3 py-2.5 text-sm text-ink outline-none focus:border-ink"
            />
          </label>

          {editingId && (
            <div className="flex flex-col gap-3 sm:col-span-2">
              <span className="text-sm font-semibold text-steel-500">
                Фотографии товара
                <span className="ml-2 font-normal text-steel-400">
                  первое фото — основное, показывается в каталоге
                </span>
              </span>
              <div className="flex flex-wrap gap-3">
                {(() => {
                  const currentImages = form.images ? form.images.split("\n").filter(Boolean) : [];
                  return currentImages.map((url, index) => (
                    <div key={url} className="group relative h-24 w-24 shrink-0 border border-steel-200">
                      <img src={url} alt="" className="h-full w-full object-cover" />
                      {index === 0 && (
                        <span className="absolute left-1 top-1 bg-ink/70 px-1.5 py-0.5 text-[9px] uppercase tracking-wide2 text-white">
                          Основное
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleImageDelete(editingId, url)}
                        aria-label="Удалить изображение"
                        className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center bg-ink/70 text-white opacity-0 transition-opacity duration-150 ease-engineered group-hover:opacity-100 hover:bg-accent"
                      >
                        <Trash2 size={13} />
                      </button>
                      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-ink/70 opacity-0 transition-opacity duration-150 ease-engineered group-hover:opacity-100">
                        <button
                          type="button"
                          disabled={index === 0}
                          onClick={() => handleImageReorder(editingId, currentImages, index, index - 1)}
                          aria-label="Переместить влево"
                          className="flex h-6 w-6 items-center justify-center text-white disabled:opacity-30"
                        >
                          <ChevronLeft size={13} />
                        </button>
                        <button
                          type="button"
                          disabled={index === currentImages.length - 1}
                          onClick={() => handleImageReorder(editingId, currentImages, index, index + 1)}
                          aria-label="Переместить вправо"
                          className="flex h-6 w-6 items-center justify-center text-white disabled:opacity-30"
                        >
                          <ChevronRight size={13} />
                        </button>
                      </div>
                    </div>
                  ));
                })()}
                <label className="flex h-24 w-24 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 border border-dashed border-steel-300 text-center text-xs text-steel-500 hover:border-ink hover:text-ink">
                  {isUploadingImage ? "Загрузка…" : "+ Добавить фото"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={isUploadingImage}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(editingId, file);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
              {imageManagerError && <p className="text-sm text-accent">{imageManagerError}</p>}
            </div>
          )}

          {editingId && (
            <div className="flex flex-col gap-3 sm:col-span-2">
              <span className="text-sm font-semibold text-steel-500">
                PDF-документ
                <span className="ml-2 font-normal text-steel-400">
                  каталог, спецификация или инструкция — показывается на странице товара
                </span>
              </span>
              <div className="flex flex-wrap items-center gap-3">
                {form.brochureUrl ? (
                  <div className="flex items-center gap-3 border border-steel-200 px-3 py-2 text-sm">
                    <a
                      href={form.brochureUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-ink underline-offset-2 hover:text-accent hover:underline"
                    >
                      Открыть текущий PDF
                    </a>
                    <button
                      type="button"
                      onClick={() => handleBrochureDelete(editingId)}
                      aria-label="Удалить PDF"
                      className="flex h-6 w-6 items-center justify-center text-steel-400 hover:bg-accent hover:text-white"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer items-center gap-2 border border-dashed border-steel-300 px-3 py-2 text-xs text-steel-500 hover:border-ink hover:text-ink">
                    {isUploadingBrochure ? "Загрузка…" : "+ Добавить PDF"}
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      disabled={isUploadingBrochure}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleBrochureUpload(editingId, file);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </div>
              {brochureError && <p className="text-sm text-accent">{brochureError}</p>}
            </div>
          )}

          <label className="flex flex-col gap-1.5 sm:col-span-2">
            <span className="text-sm font-semibold text-steel-500">
              Краткие характеристики для карточки (по одной на строку)
            </span>
            <textarea
              required
              rows={4}
              value={form.specs}
              onChange={(e) => setForm((f) => ({ ...f, specs: e.target.value }))}
              className="resize-none border border-steel-300 px-3 py-2.5 text-sm text-ink outline-none focus:border-ink"
            />
          </label>

          {(() => {
            const allSpecFields = [...COMMON_SPEC_FIELDS, ...(SUBCATEGORY_SPEC_FIELDS[form.subcategory] ?? [])];
            if (allSpecFields.length === 0) return null;
            return (
              <div className="flex flex-col gap-4 border-t border-steel-200 pt-5 sm:col-span-2">
                <p className="eyebrow text-steel-500">
                  Технические параметры — {form.subcategory}
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {allSpecFields.map((field) => (
                    <label key={field.key} className="flex flex-col gap-1.5">
                      <span className="text-sm font-semibold text-steel-500">
                        {field.label}
                        {field.unit ? `, ${field.unit}` : ""}
                      </span>
                      {field.type === "select" ? (
                        // A plain <select> restricted to field.options would
                        // silently show "not selected" for a real value this
                        // brand's own data uses but the predefined list
                        // doesn't cover (e.g. Raynaud's per-pattern finish
                        // names next to Life Concept/Tognana's fixed glaze
                        // palette in the same "color" key) — a text input
                        // with a <datalist> keeps the suggestions without
                        // hiding or blocking an out-of-list existing value.
                        <>
                          <input
                            list={`admin-spec-${field.key}`}
                            value={form.characteristics[field.key] ?? ""}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                characteristics: { ...f.characteristics, [field.key]: e.target.value },
                              }))
                            }
                            className="border border-steel-300 px-3 py-2.5 text-sm text-ink outline-none focus:border-ink"
                          />
                          <datalist id={`admin-spec-${field.key}`}>
                            {field.options?.map((opt) => <option key={opt} value={opt} />)}
                          </datalist>
                        </>
                      ) : (
                        <input
                          type={field.type === "number" ? "number" : "text"}
                          value={form.characteristics[field.key] ?? ""}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              characteristics: { ...f.characteristics, [field.key]: e.target.value },
                            }))
                          }
                          className="border border-steel-300 px-3 py-2.5 text-sm text-ink outline-none focus:border-ink"
                        />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            );
          })()}

          <label className="flex flex-col gap-1.5 sm:col-span-2">
            <span className="text-sm font-semibold text-steel-500">
              Описание для карточки товара
            </span>
            <textarea
              rows={4}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="resize-none border border-steel-300 px-3 py-2.5 text-sm text-ink outline-none focus:border-ink"
            />
          </label>

          <label className="flex flex-col gap-1.5 sm:col-span-2">
            <span className="text-sm font-semibold text-steel-500">
              Доп. изображения для галереи (по одному пути на строку)
            </span>
            <textarea
              rows={3}
              placeholder="/assets/products/thermal-01-2.svg"
              value={form.images}
              onChange={(e) => setForm((f) => ({ ...f, images: e.target.value }))}
              className="resize-none border border-steel-300 px-3 py-2.5 text-sm text-ink outline-none focus:border-ink"
            />
          </label>

          <label className="flex flex-col gap-1.5 sm:col-span-2">
            <span className="text-sm font-semibold text-steel-500">
              Видео (ID ролика YouTube или Vimeo)
            </span>
            <input
              type="text"
              placeholder="Например, dQw4w9WgXcQ (YouTube) или 123456789 (Vimeo)"
              value={form.videoId}
              onChange={(e) => setForm((f) => ({ ...f, videoId: e.target.value }))}
              className="border border-steel-300 px-3 py-2.5 text-sm text-ink outline-none focus:border-ink"
            />
            <span className="text-xs text-steel-400">
              Вставьте только ID из ссылки, не полный URL — например, из
              youtube.com/watch?v=dQw4w9WgXcQ нужен только dQw4w9WgXcQ. Число из
              одних цифр интерпретируется как Vimeo ID, всё остальное — как YouTube.
              Оставьте пустым, чтобы убрать вкладку «Видео» на странице товара.
            </span>
          </label>

          <label className="flex items-center gap-2 sm:col-span-2">
            <input
              type="checkbox"
              checked={form.onSale}
              onChange={(e) => setForm((f) => ({ ...f, onSale: e.target.checked }))}
              className="h-4 w-4 border-steel-300"
            />
            <span className="text-sm text-steel-600">Отметить как акцию (Sale)</span>
          </label>

          {error && <p className="text-sm text-accent sm:col-span-2">{error}</p>}

          <div className="flex gap-3 sm:col-span-2">
            <button type="submit" disabled={isSaving} className="btn-primary disabled:opacity-60">
              {isSaving ? "Сохранение…" : editingId ? "Сохранить изменения" : "Добавить оборудование"}
            </button>
            <button type="button" onClick={resetForm} className="btn-dark-outline">
              Отменить
            </button>
          </div>
        </form>
        )}

        <div className="mt-14">
          {deleteError && <p className="mb-4 text-sm text-accent">{deleteError}</p>}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по названию, модели, бренду, ID..."
              className="w-full border border-steel-300 px-3 py-2 text-sm text-ink placeholder-steel-400 outline-none focus:border-ink sm:w-80"
            />
            <p className="text-xs text-steel-400">
              {searchQuery.trim()
                ? `Показано ${filteredProducts.length} из ${products.length}`
                : `Всего товаров: ${products.length}`}
            </p>
          </div>

          <AdminSectionErrorBoundary>
            {isLoadingProducts ? (
              <p className="mt-6 text-sm text-steel-500">Загрузка…</p>
            ) : filteredProducts.length === 0 ? (
              <p className="mt-6 text-sm text-steel-500">Ничего не найдено по запросу «{searchQuery}».</p>
            ) : (
              <>
                <div className="mt-6 flex flex-col gap-3">
                  {pageItems.map((product) => (
                    <div
                      key={product.id}
                      className="flex flex-col gap-3 border border-steel-200 p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <p className="text-xs uppercase tracking-wide2 text-steel-500">
                          {CATEGORY_LABELS[product.category] ?? product.category}
                          {product.subcategory ? ` / ${product.subcategory}` : ""} · {product.id}
                        </p>
                        <p className="font-sans text-base font-semibold text-ink">
                          {product.brand ? `${product.brand} — ` : ""}
                          {product.name}
                        </p>
                        <p className="text-xs text-steel-500">
                          {product.model}
                          {product.price !== undefined
                            ? ` · ${product.price.toLocaleString("ru-RU")} ₸`
                            : " · цена по запросу"}
                          {product.stockQuantity !== undefined
                            ? ` · в наличии: ${product.stockQuantity} шт.`
                            : " · под заказ"}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <button
                          type="button"
                          onClick={() => startEdit(product)}
                          aria-label="Редактировать"
                          className="flex h-9 w-9 items-center justify-center text-ink transition-colors duration-150 ease-engineered hover:bg-steel-900/5 active:bg-steel-900/5"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(product.id)}
                          aria-label="Удалить"
                          className="flex h-9 w-9 items-center justify-center text-accent transition-colors duration-150 ease-engineered hover:bg-accent/10 active:bg-accent/10"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {pageCount > 1 && (
                  <div className="mt-6 flex items-center justify-center gap-4">
                    <button
                      type="button"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                      className="btn-dark-outline disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Назад
                    </button>
                    <span className="text-sm text-steel-500">
                      Страница {page} из {pageCount}
                    </span>
                    <button
                      type="button"
                      disabled={page >= pageCount}
                      onClick={() => setPage((p) => p + 1)}
                      className="btn-dark-outline disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Далее
                    </button>
                  </div>
                )}
              </>
            )}
          </AdminSectionErrorBoundary>
        </div>
      </div>
    </section>
  );
}
